import _ from 'lodash';
import { computed } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useFilterSettingsStore, usePipedriveStore, useTenantInMarketStore } from '../..';
import useDealsInsideShape from '../../hooks/useDealsInsideShape';
import useDealsWithFocusArea from '../../hooks/useDealsWithFocusArea';
import useDealsWithGlobalFilters from '../../hooks/useDealsWithGlobalFilters';
import { DealStatus } from '../../interfaces/IDeal';
import { IDetailsTabProps } from "../../interfaces/IDetailsTabProps";
import { calculateVariance, formatNumber } from '../../utils/utils';
import DealsGrid from '../deals/DealsGrid';

function TenantInMarketDetailsDealsTab({ visible, setEntityCount }: IDetailsTabProps) {
    const { id } = useParams();
    const pipedriveStore = usePipedriveStore();
    const tenantInMarketStore = useTenantInMarketStore();
    const tenantInMarket = useMemo(() => computed(() => tenantInMarketStore.tenantsInMarket.find(tim => tim.id.toString() === id)!), [id]).get();
    const filterSettingsStore = useFilterSettingsStore();
    const allDeals = useDealsWithFocusArea(useDealsWithGlobalFilters(useDealsInsideShape(pipedriveStore.deals, tenantInMarket.shape)));
    const showLostDeals = filterSettingsStore.filters.tenantInMarketSettings.showLostDeals;
    const landNeeded = useMemo(() => (tenantInMarket.landAreaMin + tenantInMarket.landAreaMax) / 2, [tenantInMarket]);
    const buildNeeded = useMemo(() => (tenantInMarket.buildingAreaMin + tenantInMarket.buildingAreaMax) / 2, [tenantInMarket]);
    const isLandOnly = !buildNeeded;
    const deals = useMemo(() => {
        let filtered = showLostDeals ? allDeals : allDeals.filter(d => d.status !== DealStatus.Lost);
        if (isLandOnly)
            filtered = filtered.filter(d => !d.buildingSize);
        else
            filtered = filtered.filter(d => !!d.buildingSize);
        return filtered;
    }, [allDeals, showLostDeals, isLandOnly]);
    const dealsWithVariance = useMemo(() => deals.map(item => ({
        ...item,
        variance: calculateVariance(item.landSizeAcres, item.buildingSize, landNeeded, buildNeeded),
    })), [deals, landNeeded, buildNeeded]);

    const sortedDeals = useMemo(() => _.orderBy(dealsWithVariance, d => d.variance, "asc"), [dealsWithVariance]);

    useEffect(() => setEntityCount(deals.length), [deals]);

    if (!visible)
        return null;

    return (
        <DealsGrid
            deals={sortedDeals}
            additionalHeadersBefore={[{
                align: "left",
                titleStrings: ["VARIANCE"],
                getValue: item => item.variance,
                getLabel: item => item.variance !== null ? formatNumber(item.variance) : "",
            }]}
        />
    );
}

export default observer(TenantInMarketDetailsDealsTab);